// Path emission must mirror the walker in DefaultCaseDetailsPage
// (`outputFieldLabels`): array containers contribute `<id>[0]`, object
// containers contribute `<id>`, and leaves contribute their plain id. This
// matches the engine output (e.g. `drug[0].coverage.coverage_status`) so the
// `allowedPaths` set lookups hit.
type FieldLike = {
  id?: string;
  label?: string;
  type?: string;
  fields?: FieldLike[];
};

function pruneChildren(
  children: FieldLike[] | undefined,
  basePath: string,
  allowed: ReadonlySet<string>,
): FieldLike[] {
  const kept: FieldLike[] = [];
  for (const child of children ?? []) {
    const pruned = pruneField(child, basePath, allowed);
    if (pruned) kept.push(pruned);
  }
  return kept;
}

function pruneField(
  field: FieldLike,
  basePath: string,
  allowed: ReadonlySet<string>,
): FieldLike | null {
  if (!field.id) return null;
  const isContainer = field.type === 'array' || field.type === 'object';
  const segment = field.type === 'array' ? `${field.id}[0]` : field.id;
  const path = basePath ? `${basePath}.${segment}` : segment;

  if (isContainer) {
    const keptChildren = pruneChildren(field.fields, path, allowed);
    return keptChildren.length === 0 ? null : { ...field, fields: keptChildren };
  }
  return allowed.has(path) ? field : null;
}

/**
 * Prunes a form-schema group tree to only the leaves whose computed engine
 * path is in `allowedPaths`. Drives the case-detail Output panel's Missed /
 * Required filter tabs against the disposition engine's `missing_fields` /
 * `required_params` lists.
 *
 * Container fields (array/object) survive iff at least one descendant
 * survives. Empty groups are dropped entirely. Pure — never mutates the
 * input.
 *
 * @param groups        Schema groups, each with a `fields` array of
 *                      `FieldLike` nodes. The generic `G` preserves any
 *                      extra group-level fields (`label`, etc.) via spread.
 * @param allowedPaths  Set of engine field paths to keep. Array indices may be
 *                      any instance (e.g. `procedure[1]....`); they are
 *                      normalized to `[0]`-form internally before matching.
 * @returns A new array of groups with the same shape as the input, fields
 *          filtered down to surviving leaves and their ancestors. Returns
 *          `[]` when `allowedPaths` is empty.
 *
 * @example
 *   filterOutputGroupsByPaths(groups, new Set(['drug[0].coverage.coverage_status']))
 *     // → groups containing just the surviving drug → coverage → coverage_status chain
 */
export function filterOutputGroupsByPaths<G extends { fields: FieldLike[] }>(
  groups: G[],
  allowedPaths: ReadonlySet<string>,
): G[] {
  if (allowedPaths.size === 0) return [];
  // The engine emits real array indices (e.g. `procedure[1].coverage.oopmax_applies`),
  // but `pruneField` only ever generates `[0]`-form schema paths. Normalize every
  // index to `[0]` so a leaf missed on ANY instance still matches its schema path
  // (mirrors resolveAskNextLabel.ts). Without this, a container whose only surviving
  // leaf is on a non-zero instance is pruned away entirely — dropping the section and
  // truncating the parent "captured" counter. Idempotent for already-`[0]`-form input.
  const normalizedAllowed = new Set([...allowedPaths].map(path => path.replace(/\[\d+\]/g, '[0]')));
  const result: G[] = [];
  for (const group of groups) {
    const keptFields: FieldLike[] = [];
    for (const field of group.fields) {
      const pruned = pruneField(field, '', normalizedAllowed);
      if (pruned) keptFields.push(pruned);
    }
    if (keptFields.length > 0) {
      result.push({ ...group, fields: keptFields });
    }
  }
  return result;
}
